import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'
import { Check, LogOut } from 'lucide-react'
import { useLogout } from '@/shared/hooks/api/auth'
import { useSession } from '@/shared/hooks/useSession'
import { AuthInterfaceLanguageSelect } from '@/features/auth/auth-interface-language-select'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ThemeSwitch } from '@/components/theme-switch'
import { cn } from '@/shared/lib/utils'

function OperatorMark() {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-lg font-bold tracking-tight">
        Artem<span className="text-green-600">CBAM</span>
      </span>
      <span className="text-sm font-semibold uppercase text-muted-foreground">
        Operator
      </span>
    </div>
  )
}

type WizardVisualStep = 1 | 2 | 3

function OnboardingStepsOverview({
  currentStep,
}: {
  currentStep: WizardVisualStep
}) {
  const { t } = useTranslation('onboarding')

  const stepsMeta = [
    {
      step: 1 as const,
      title: t('stepVerifyEmailTitle'),
      description: t('stepVerifyEmailDescription'),
    },
    {
      step: 2 as const,
      title: t('stepOrganizationDetailsTitle'),
      description: t('stepOrganizationDetailsDescription'),
    },
    {
      step: 3 as const,
      title: t('stepPendingTitle'),
      description: t('stepPendingDescription'),
    },
  ]

  return (
    <ol className="space-y-0" aria-label={t('overviewAriaLabel')}>
      {stepsMeta.map((item, index) => {
        const isComplete = currentStep > item.step
        const isCurrent = currentStep === item.step
        const isLast = index === stepsMeta.length - 1

        return (
          <li
            key={item.step}
            className="flex gap-4"
            aria-current={isCurrent ? 'step' : undefined}
          >
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                  isComplete &&
                    'border-primary bg-primary text-primary-foreground',
                  isCurrent &&
                    !isComplete &&
                    'border-primary bg-background text-primary',
                  !isCurrent &&
                    !isComplete &&
                    'border-muted-foreground/35 text-muted-foreground'
                )}
              >
                {isComplete ? (
                  <Check className="size-4" strokeWidth={2.5} aria-hidden />
                ) : (
                  item.step
                )}
              </span>
              {!isLast && (
                <span
                  className={cn(
                    'my-2 min-h-14 w-px shrink-0',
                    isComplete ? 'bg-primary' : 'bg-border'
                  )}
                  aria-hidden
                />
              )}
            </div>
            <div className={cn('min-w-0 pb-10', isLast && 'pb-0')}>
              <p
                className={cn(
                  'leading-snug font-medium',
                  isCurrent && 'text-foreground',
                  !isCurrent && 'text-muted-foreground'
                )}
              >
                {item.title}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {item.description}
              </p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function getWizardVisualStep(pathname: string): WizardVisualStep {
  if (pathname.includes('/onboarding/pending')) {
    return 3
  }
  if (pathname.includes('/onboarding/organization-details')) {
    return 2
  }
  return 1
}

function getMainHeaderCopy(
  pathname: string,
  translate: (key: string) => string
): { title: string; description: string } {
  if (pathname.includes('/onboarding/pending')) {
    return {
      title: translate('success.title'),
      description: translate('success.subtitle'),
    }
  }
  if (pathname.includes('/onboarding/organization-details')) {
    return {
      title: translate('stepOrganizationDetailsTitle'),
      description: translate('stepOrganizationDetailsDescription'),
    }
  }
  return {
    title: translate('stepVerifyEmailTitle'),
    description: translate('stepVerifyEmailDescription'),
  }
}

export function OnboardingLayout() {
  const { t } = useTranslation('onboarding')
  const { t: tCommon } = useTranslation('common')
  const { session } = useSession()
  const { mutate: logout, isPending: isLoggingOut } = useLogout()
  const location = useLocation()
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false)

  const wizardVisualStep = useMemo(() => {
    const fromRoute = getWizardVisualStep(location.pathname)
    if (fromRoute === 2 && !session.isEmailVerified) {
      return 1
    }
    return fromRoute
  }, [location.pathname, session.isEmailVerified])

  const mainHeader = useMemo(
    () => getMainHeaderCopy(location.pathname, (key) => t(key)),
    [location.pathname, t]
  )

  return (
    <>
      <div className="grid min-h-svh w-full grid-cols-1 lg:grid-cols-[minmax(280px,400px)_minmax(0,1fr)]">
        <aside
          className={cn(
            'flex min-h-svh flex-col border-b border-border bg-muted/40 px-5 py-8 sm:px-8',
            'lg:min-h-0 lg:border-e lg:border-b-0 lg:py-12 lg:ps-10 lg:pe-8'
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-8">
              <OperatorMark />
            </div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {t('title')}
            </h1>
            <div className="mt-8">
              <OnboardingStepsOverview currentStep={wizardVisualStep} />
            </div>
          </div>
          <div className="mt-auto flex shrink-0 flex-col gap-4 pt-10">
            <Button
              type="button"
              variant="ghost"
              className="h-auto max-w-[220px] justify-start gap-2 px-0 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setIsSignOutDialogOpen(true)
              }}
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              {t('signOut')}
            </Button>
            <div className="flex max-w-[240px] items-center justify-between gap-3 sm:max-w-[280px]">
              <AuthInterfaceLanguageSelect className="max-w-[220px]" />
              <ThemeSwitch />
            </div>
          </div>
        </aside>

        <main className="flex min-h-0 flex-col overflow-y-auto">
          <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-4 py-8 sm:max-w-xl sm:px-8 lg:py-12">
            <header className="mb-8 space-y-2">
              <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
                {mainHeader.title}
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                {mainHeader.description}
              </p>
            </header>
            <Outlet />
          </div>
        </main>
      </div>

      <ConfirmDialog
        open={isSignOutDialogOpen}
        onOpenChange={setIsSignOutDialogOpen}
        title={t('signOutDialogTitle')}
        desc={t('signOutDialogDescription')}
        cancelBtnText={tCommon('actions.cancel')}
        confirmText={tCommon('actions.signOut')}
        destructive
        isLoading={isLoggingOut}
        handleConfirm={() => {
          logout()
        }}
      />
    </>
  )
}
